if (!customElements.get('flip-to-win')) {
  customElements.define(
    'flip-to-win',
    class FlipToWin extends HTMLElement {
      constructor() {
        super();
        this.order = [];
        this.onCardClick = this.onCardClick.bind(this);
      }

      connectedCallback() {
        this.total = parseInt(this.dataset.total, 10) || 0;
        this.cards = Array.from(this.querySelectorAll('[data-card]'));
        this.prizes = Array.from(this.querySelectorAll('template[data-prize]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.heading = this.querySelector('[data-heading]');
        this.subline = this.querySelector('[data-subline]');
        this.progress = this.querySelector('[data-progress]');
        this.reward = this.querySelector('[data-reward]');
        this.claimButton = this.querySelector('[data-claim]');
        this.onClaimClick = this.onClaimClick.bind(this);
        this.claimButton?.addEventListener('click', this.onClaimClick);

        this.cards.forEach((card, i) => {
          card.dataset.index = i;
          card.addEventListener('click', this.onCardClick);
        });

        if (this.dataset.previewCompleted === 'true') this.previewCompleted();
        this.render();
      }

      disconnectedCallback() {
        this.cards.forEach((card) => card.removeEventListener('click', this.onCardClick));
        this.claimButton?.removeEventListener('click', this.onClaimClick);
        clearTimeout(this.confettiTimer);
        if (this.canvas) this.canvas.remove();
      }

      get done() {
        return this.total > 0 && this.order.length >= this.total;
      }

      onCardClick(event) {
        this.flip(parseInt(event.currentTarget.dataset.index, 10), true);
      }

      flip(index, animate) {
        const card = this.cards[index];
        if (!card || card.classList.contains('is-open') || this.done) return;

        const prize = this.prizes[this.order.length];
        const front = card.querySelector('[data-card-front]');
        front.replaceChildren(prize.content.cloneNode(true));
        card.classList.add('is-open');
        card.setAttribute('aria-label', prize.dataset.title);
        this.order.push(index);

        this.render();
        if (this.done && animate) this.finish();
      }

      // Mirrors the design's showCompleted state: cards 0, 2 and 5 flipped
      previewCompleted() {
        const preferred = [0, 2, 5, 7].filter((i) => i < this.cards.length);
        const rest = this.cards.map((_, i) => i).filter((i) => !preferred.includes(i));
        preferred
          .concat(rest)
          .slice(0, this.total)
          .forEach((i) => this.flip(i, false));
      }

      render() {
        const count = this.order.length;
        const left = this.total - count;
        const fill = (text) =>
          (text || '').replace(/\[count\]/g, count).replace(/\[left\]/g, left).replace(/\[total\]/g, this.total);

        let heading;
        if (this.done) heading = this.dataset.headingDone;
        else if (count === 0) heading = this.dataset.headingStart.replace(/\[count\]/g, this.total);
        else heading = left === 1 ? this.dataset.headingProgressOne : this.dataset.headingProgress;

        if (this.heading) this.heading.textContent = fill(heading);
        if (this.subline) this.subline.textContent = this.done ? this.dataset.sublineDone : this.dataset.subline;
        if (this.progress) this.progress.textContent = fill(this.dataset.progressText);
        this.dots.forEach((dot, i) => dot.classList.toggle('is-filled', i < count));

        this.classList.toggle('is-done', this.done);
        this.cards.forEach((card) => {
          card.setAttribute('aria-disabled', card.classList.contains('is-open') || this.done ? 'true' : 'false');
        });
        if (this.done && this.reward) this.reward.hidden = false;
      }

      // Applies the discount code to the cart, then follows the button link.
      // The href is Shopify's /discount/ URL, which still works if this request fails.
      async onClaimClick(event) {
        const button = event.currentTarget;
        const { discountCode, target } = button.dataset;
        if (!discountCode || !target) return;

        event.preventDefault();
        if (button.getAttribute('aria-busy') === 'true') return;
        button.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: discountCode }),
          });
          if (!response.ok) throw new Error(`Applying discount failed: ${response.status}`);
          window.location.href = target;
        } catch (error) {
          console.error(error);
          window.location.href = button.href;
        }
      }

      finish() {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.confettiTimer = setTimeout(() => {
          if (this.dataset.confetti === 'true' && !reducedMotion) this.confetti();
          this.reward?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
        }, 500);
      }

      confetti() {
        if (this.canvas) this.canvas.remove();
        const cv = document.createElement('canvas');
        const dpr = window.devicePixelRatio || 1;
        const W = window.innerWidth;
        const H = window.innerHeight;
        cv.width = W * dpr;
        cv.height = H * dpr;
        cv.className = 'flip-to-win__confetti';
        cv.setAttribute('aria-hidden', 'true');
        document.body.appendChild(cv);
        this.canvas = cv;

        const ctx = cv.getContext('2d');
        ctx.scale(dpr, dpr);
        const cols = (this.dataset.confettiColors || '#D62E55,#F1C9C9,#141414,#ffffff,#e8798f').split(',');
        const ps = Array.from({ length: 280 }, (_, i) => {
          const side = i % 2 ? 1 : -1;
          const a = (side > 0 ? -2.2 : -0.95) + (Math.random() - 0.5) * 0.6;
          const v = 13 + Math.random() * 12;
          return {
            x: side > 0 ? W : 0,
            y: H * 0.75,
            vx: Math.cos(a) * v,
            vy: Math.sin(a) * v,
            w: 10 + Math.random() * 9,
            h: 7 + Math.random() * 9,
            r: Math.random() * 6,
            vr: (Math.random() - 0.5) * 0.3,
            c: cols[i % cols.length],
            heart: i % 6 === 0,
          };
        });

        const t0 = performance.now();
        const step = (now) => {
          const el = now - t0;
          ctx.clearRect(0, 0, W, H);
          ps.forEach((p) => {
            p.vy += 0.22;
            p.vx *= 0.988;
            p.vy *= 0.988;
            p.x += p.vx;
            p.y += p.vy;
            p.r += p.vr;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.r);
            ctx.globalAlpha = Math.max(0, 1 - Math.max(0, el - 2000) / 600);
            ctx.fillStyle = p.c;
            if (p.heart) {
              ctx.font = '26px serif';
              ctx.fillText('♥', -10, 9);
            } else {
              ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.r * 2)));
            }
            ctx.restore();
          });
          if (el < 2600 && cv.isConnected) {
            requestAnimationFrame(step);
          } else {
            cv.remove();
            if (this.canvas === cv) this.canvas = null;
          }
        };
        requestAnimationFrame(step);
      }
    }
  );
}
